/**
 * @module immersive/Lobby
 * @description Send Love Live lobby (vision §11): stage hero with Cupid, the
 * Live Now strip (causes with activity first), "Find your cause" search +
 * facets (same pure query functions as Tiers 1–2), and stage cards with
 * Love Meters and live activity bubbles.
 */
import { useMemo, useState, type JSX } from "react";
import { useGiving } from "../app/GivingContext";
import { navigate } from "../app/router";
import { CATEGORY_LABELS, PARISH_LABELS, URGENCY_LABELS } from "../config/taxonomy";
import { applyQuery, countBy, EMPTY_QUERY, hasActiveFilters, toggle } from "../domain/query";
import type { Cause, CauseQuery, CategoryId, ParishId, SortId, UrgencyId } from "../domain/types";
import { formatUsd, percentRaised } from "../lib/format";
import { Icon } from "../components/Icons";
import { useStage } from "./StageContext";

/** Sort labels (same four as Tiers 1–2, brief §4.3). */
const SORT_LABELS: Readonly<Record<SortId, string>> = { popular: "Most Popular", urgent: "Most Urgent", newest: "Newest", updated: "Recently Updated" };

/** Love Meter: the stage's progress bar (vision glossary). */
export function LoveMeter({ cause }: { cause: Cause }): JSX.Element {
    const pct: number = percentRaised(cause.funding.raisedUsd, cause.funding.goalUsd);
    return (
        <div className="love-meter" role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} aria-label={`Love Meter ${pct}% (sample figures)`}>
            <div className="love-meter__track"><div className="love-meter__fill" style={{ width: `${pct}%` }} /></div>
            <span className="love-meter__label">{formatUsd(cause.funding.raisedUsd)} of {formatUsd(cause.funding.goalUsd)}</span>
        </div>
    );
}

/** Activity bubble: "♥ 3 in the last minute" (simulated). Nothing when quiet. */
export function ActivityBubble({ causeId }: { causeId: string }): JSX.Element | null {
    const { recentCount } = useStage();
    const n: number = recentCount(causeId);
    if (n === 0) return null;
    return <span className="activity-bubble" key={n}><span className="activity-bubble__dot" /> {n} gift{n === 1 ? "" : "s"} this minute</span>;
}

/** One stage card. */
function StageCard({ cause, size = "grid" }: { cause: Cause; size?: "strip" | "grid" }): JSX.Element {
    return (
        <button type="button" className={`stage-card stage-card--${size}`} onClick={() => navigate("stage", cause.id)} aria-label={`Open the ${cause.title} stage`}>
            <img src={cause.image.src} alt="" loading="lazy" className="stage-card__img" />
            <span className="stage-card__shade" />
            <span className="stage-card__top">
                <ActivityBubble causeId={cause.id} />
            </span>
            <span className="stage-card__body">
                <span className="stage-card__partner">{cause.partner}</span>
                <span className="stage-card__title">{cause.title}</span>
                <LoveMeter cause={cause} />
            </span>
        </button>
    );
}

/** The lobby page. */
export function Lobby(): JSX.Element {
    const { causes } = useGiving();
    const { recentCount } = useStage();
    const [query, setQuery] = useState<CauseQuery>(EMPTY_QUERY);
    const list: Cause[] = causes ?? [];

    // Live Now: most recent activity first, then featured (vision §11).
    const liveNow: Cause[] = useMemo(
        () => [...list].sort((a, b) => recentCount(b.id) - recentCount(a.id) || Number(b.featured) - Number(a.featured)).slice(0, 6),
        [list, recentCount],
    );
    const results: Cause[] = useMemo(() => applyQuery(list, query), [list, query]);
    const counts = useMemo(() => countBy<CategoryId>(list, (c) => c.category), [list]);
    const urgencyCounts = useMemo(() => countBy<UrgencyId>(list, (c) => c.urgency), [list]);
    const parishCounts = useMemo(() => countBy<ParishId>(list, (c) => c.parish), [list]);

    return (
        <main className="lobby">
            <section className="lobby-hero">
                <div className="container lobby-hero__inner">
                    <div className="lobby-hero__copy">
                        <p className="lobby-hero__kicker">Send Love Live</p>
                        <h1 className="lobby-hero__title">Every cause is a stage. Every gift is a celebration.</h1>
                        <p className="lobby-hero__lede">Step onto a cause's stage, shoot a Legend Arrow, and watch your love land in Jamaica. You always give in US dollars.</p>
                        <button type="button" className="btn btn--gold btn--lg" onClick={() => document.getElementById("live-now")?.scrollIntoView({ behavior: "smooth" })}>
                            See who's live <Icon name="arrow-right" />
                        </button>
                    </div>
                    <img className="lobby-hero__cupid" src="cupid/cupid-draw.webp" alt="Ja-merican Cupid drawing a heart-tipped arrow" width={420} height={424} />
                </div>
            </section>

            <section id="live-now" className="container lobby-section" aria-labelledby="live-title">
                <div className="lobby-section__head">
                    <h2 id="live-title" className="lobby-section__title"><span className="live-dot" /> Live Now</h2>
                    <span className="stage-flag">Simulated activity</span>
                </div>
                <div className="live-strip">
                    {liveNow.map((c: Cause) => <StageCard key={c.id} cause={c} size="strip" />)}
                </div>
            </section>

            <section className="container lobby-section" aria-labelledby="find-title">
                <div className="lobby-section__head">
                    <h2 id="find-title" className="lobby-section__title">Find your cause</h2>
                    <span className="stage-flag">Sample causes</span>
                </div>
                <div className="lobby-find">
                    <label className="lobby-find__search">
                        <Icon name="search" />
                        <span className="visually-hidden">Search causes</span>
                        <input type="search" value={query.text} placeholder="Search causes, partners, places" onChange={(e) => setQuery({ ...query, text: e.target.value })} />
                    </label>
                    <div className="lobby-find__chips" role="group" aria-label="Category">
                        {(Object.keys(CATEGORY_LABELS) as CategoryId[]).map((id: CategoryId) => (
                            <button key={id} type="button" className="stage-chip" aria-pressed={query.categories.includes(id)} disabled={(counts.get(id) ?? 0) === 0}
                                onClick={() => setQuery({ ...query, categories: toggle(query.categories, id) })}>{CATEGORY_LABELS[id]} <span className="stage-chip__count">{counts.get(id) ?? 0}</span></button>
                        ))}
                    </div>
                    <div className="lobby-find__chips" role="group" aria-label="Urgency, place and order">
                        {(Object.keys(URGENCY_LABELS) as UrgencyId[]).map((id: UrgencyId) => (
                            <button key={id} type="button" className="stage-chip" aria-pressed={query.urgencies.includes(id)}
                                onClick={() => setQuery({ ...query, urgencies: toggle(query.urgencies, id) })}>{URGENCY_LABELS[id]} <span className="stage-chip__count">{urgencyCounts.get(id) ?? 0}</span></button>
                        ))}
                        <label>
                            <span className="visually-hidden">Parish</span>
                            <select className="stage-select" value={query.parishes[0] ?? ""} onChange={(e) => setQuery({ ...query, parishes: e.target.value === "" ? [] : [e.target.value as ParishId] })}>
                                <option value="">All parishes</option>
                                {(Object.keys(PARISH_LABELS) as ParishId[]).filter((id) => parishCounts.has(id)).map((id: ParishId) => (
                                    <option key={id} value={id}>{PARISH_LABELS[id]} ({parishCounts.get(id)})</option>
                                ))}
                            </select>
                        </label>
                        <label>
                            <span className="visually-hidden">Sort by</span>
                            <select className="stage-select" value={query.sort} onChange={(e) => setQuery({ ...query, sort: e.target.value as SortId })}>
                                {(Object.keys(SORT_LABELS) as SortId[]).map((id: SortId) => <option key={id} value={id}>{SORT_LABELS[id]}</option>)}
                            </select>
                        </label>
                        {hasActiveFilters(query) && (
                            <button type="button" className="stage-clear" onClick={() => setQuery({ ...EMPTY_QUERY, sort: query.sort })}><Icon name="close" size={14} /> Clear filters</button>
                        )}
                    </div>
                </div>
                <p className="lobby-count" aria-live="polite">Showing {results.length} of {list.length} stages</p>
                <div className="stage-grid">
                    {results.map((c: Cause) => <StageCard key={c.id} cause={c} />)}
                </div>
                {results.length === 0 && <p className="lobby-count">No stages match yet. Try fewer filters.</p>}
            </section>
        </main>
    );
}
